import { useEffect, useRef, useState } from "react";

/**
 * Seamless looping video with cross-fade at the loop seam.
 * Two stacked <video> elements; when A nears its end, B fades in and restarts,
 * then they swap roles. No visible cut, no black flash.
 *
 * fadeMs — how long the crossfade lasts (also the overlap window before loop end).
 */
export function LoopVideo({
  src,
  fadeMs = 800,
  className = "",
  poster,
}: {
  src: string;
  fadeMs?: number;
  className?: string;
  poster?: string;
}) {
  const aRef = useRef<HTMLVideoElement>(null);
  const bRef = useRef<HTMLVideoElement>(null);
  const [activeA, setActiveA] = useState(true);

  useEffect(() => {
    const a = aRef.current;
    const b = bRef.current;
    if (!a || !b) return;

    let cancelled = false;
    const fadeSec = fadeMs / 1000;

    const onTimeUpdate = (e: Event) => {
      const v = e.currentTarget as HTMLVideoElement;
      if (cancelled || !v.duration || isNaN(v.duration)) return;
      const remaining = v.duration - v.currentTime;
      if (remaining <= fadeSec) {
        const other = v === a ? b : a;
        if (other.paused) {
          other.currentTime = 0;
          const p = other.play();
          if (p && typeof p.catch === "function") p.catch(() => {});
          setActiveA(v === b); // flip: the *other* becomes active
        }
      }
    };

    const onEnded = (e: Event) => {
      // loop attr is not set; we reset manually so the fade already happened
      const v = e.currentTarget as HTMLVideoElement;
      v.pause();
      v.currentTime = 0;
    };

    a.addEventListener("timeupdate", onTimeUpdate);
    b.addEventListener("timeupdate", onTimeUpdate);
    a.addEventListener("ended", onEnded);
    b.addEventListener("ended", onEnded);

    // kick off A
    a.play().catch(() => {});

    return () => {
      cancelled = true;
      a.removeEventListener("timeupdate", onTimeUpdate);
      b.removeEventListener("timeupdate", onTimeUpdate);
      a.removeEventListener("ended", onEnded);
      b.removeEventListener("ended", onEnded);
    };
  }, [fadeMs, src]);

  const base =
    "absolute inset-0 w-full h-full object-cover transition-opacity ease-linear";
  const dur = { transitionDuration: `${fadeMs}ms` };

  return (
    <div className={`relative w-full h-full overflow-hidden ${className}`}>
      <video
        ref={aRef}
        src={src}
        muted
        playsInline
        preload="auto"
        poster={poster}
        className={`${base} ${activeA ? "opacity-100" : "opacity-0"}`}
        style={dur}
      />
      <video
        ref={bRef}
        src={src}
        muted
        playsInline
        preload="auto"
        poster={poster}
        className={`${base} ${activeA ? "opacity-0" : "opacity-100"}`}
        style={dur}
      />
    </div>
  );
}
